import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const TeacherMarks = () => {
  const { user } = useAuth();
  const [marks, setMarks] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMarks = async () => {
      try {
        // Fetch teacher profile first
        const profileResponse = await api.get(`/teachers/user/${user?._id}`);
        
        // Fetch marks assigned to this teacher
        const marksResponse = await api.get(`/results/teacher/${profileResponse.data.data._id}`);
        setMarks(marksResponse.data.data);
      } catch (error) {
        console.error('Error fetching marks:', error);
        // Fallback to mock data if API fails
        const mockMarks = [
          {
            id: 1,
            rollNumber: '10A001',
            name: 'John Smith',
            className: 'Grade 10',
            subject: 'Mathematics',
            examType: 'Mid Term',
            marks: 85,
            maxMarks: 100,
            percentage: 85
          },
          {
            id: 2,
            rollNumber: '10A002',
            name: 'Emma Johnson',
            className: 'Grade 10',
            subject: 'Mathematics',
            examType: 'Mid Term',
            marks: 92,
            maxMarks: 100,
            percentage: 92
          },
          {
            id: 3,
            rollNumber: '10A003',
            name: 'Michael Brown',
            className: 'Grade 10',
            subject: 'Mathematics',
            examType: 'Mid Term',
            marks: 78,
            maxMarks: 100,
            percentage: 78
          },
          {
            id: 4,
            rollNumber: '10A004',
            name: 'Sarah Davis',
            className: 'Grade 10',
            subject: 'Mathematics',
            examType: 'Mid Term',
            marks: 95,
            maxMarks: 100,
            percentage: 95
          },
          {
            id: 5,
            rollNumber: '10A005',
            name: 'David Wilson',
            className: 'Grade 10',
            subject: 'Mathematics',
            examType: 'Mid Term',
            marks: 88,
            maxMarks: 100,
            percentage: 88
          }
        ];
        setMarks(mockMarks);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchMarks();
    }
  }, [user]);

  const filteredMarks = marks.filter(mark => {
    return (!selectedClass || mark.className === selectedClass) &&
           (!selectedSubject || mark.subject === selectedSubject) &&
           (!selectedExam || mark.examType === selectedExam);
  });

  const uniqueClasses = [...new Set(marks.map(mark => mark.className))];
  const uniqueSubjects = [...new Set(marks.map(mark => mark.subject))];
  const uniqueExams = [...new Set(marks.map(mark => mark.examType))];

  const handleMarkChange = (markId, newMark) => {
    const updatedMarks = marks.map(mark => {
      if (mark.id === markId) {
        const numericMark = parseInt(newMark) || 0;
        const percentage = mark.maxMarks ? Math.round((numericMark / mark.maxMarks) * 100) : 0;
        return {
          ...mark,
          marks: numericMark,
          percentage: percentage
        };
      }
      return mark;
    });
    setMarks(updatedMarks);
  };

  const handleSaveMarks = async () => {
    try {
      // Prepare marks data for API submission
      const marksPayload = marks.map(mark => ({
        studentId: mark.studentId,
        marks: mark.marks,
        examType: mark.examType,
        subject: mark.subject,
        class: mark.className
      }));
      
      await api.put('/results/bulk', { marksData: marksPayload });
      alert('Marks saved successfully!');
    } catch (error) {
      console.error('Error saving marks:', error);
      alert('Failed to save marks. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Enter Marks</h1>
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 mb-2">Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Classes</option>
                {uniqueClasses.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Subject</label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Subjects</option>
                {uniqueSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2">Exam Type</label>
              <select
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Exams</option>
                {uniqueExams.map(exam => (
                  <option key={exam} value={exam}>{exam}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleSaveMarks}
                className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-blue-700 transition"
              >
                Save Marks
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roll Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Marks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMarks.map((mark) => (
                  <tr key={mark.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mark.rollNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{mark.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mark.className}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mark.subject}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mark.examType}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        min="0"
                        max={mark.maxMarks}
                        value={mark.marks}
                        onChange={(e) => handleMarkChange(mark.id, e.target.value)}
                        className="w-20 border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mark.maxMarks}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mark.percentage}%</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        mark.percentage >= 90 ? 'bg-purple-100 text-purple-800' :
                        mark.percentage >= 80 ? 'bg-green-100 text-green-800' :
                        mark.percentage >= 70 ? 'bg-blue-100 text-blue-800' :
                        mark.percentage >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        mark.percentage >= 50 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {mark.percentage >= 90 ? 'A+' :
                         mark.percentage >= 80 ? 'A' :
                         mark.percentage >= 70 ? 'B+' :
                         mark.percentage >= 60 ? 'B' :
                         mark.percentage >= 50 ? 'C' : 'F'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Grade Distribution</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>A+ Grade (90-100%)</span>
                <span className="font-medium text-purple-600">
                  {marks.filter(m => m.percentage >= 90).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>A Grade (80-89%)</span>
                <span className="font-medium text-green-600">
                  {marks.filter(m => m.percentage >= 80 && m.percentage < 90).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>B+ Grade (70-79%)</span>
                <span className="font-medium text-blue-600">
                  {marks.filter(m => m.percentage >= 70 && m.percentage < 80).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>B Grade (60-69%)</span>
                <span className="font-medium text-yellow-600">
                  {marks.filter(m => m.percentage >= 60 && m.percentage < 70).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>C Grade (50-59%)</span>
                <span className="font-medium text-orange-600">
                  {marks.filter(m => m.percentage >= 50 && m.percentage < 60).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>F Grade (&lt;50%)</span>
                <span className="font-medium text-red-600">
                  {marks.filter(m => m.percentage < 50).length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Average Score</span>
                <span className="font-medium">
                  {marks.length > 0 ? (marks.reduce((sum, m) => sum + m.percentage, 0) / marks.length).toFixed(1) : 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Highest Score</span>
                <span className="font-medium text-green-600">
                  {marks.length > 0 ? Math.max(...marks.map(m => m.percentage)) : 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Lowest Score</span>
                <span className="font-medium text-red-600">
                  {marks.length > 0 ? Math.min(...marks.map(m => m.percentage)) : 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Students</span>
                <span className="font-medium">
                  {marks.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherMarks;